import { snakeCaseKeys } from './utils.js';
var AccessTokenErrorStatus;
(function (AccessTokenErrorStatus) {
    AccessTokenErrorStatus[AccessTokenErrorStatus["Unauthorized"] = 401] = "Unauthorized";
    AccessTokenErrorStatus[AccessTokenErrorStatus["NotFound"] = 404] = "NotFound";
})(AccessTokenErrorStatus || (AccessTokenErrorStatus = {}));
class AccessTokenError extends Error {
    status;
    response;
    constructor(status, response = {}) {
        super(['Authentication service error', response.msg].filter(Boolean).join(': '));
        this.status = status;
        this.response = response;
        this.name = 'WAuth2Service.AccessTokenError';
    }
}
class ServiceFailureError extends Error {
    status;
    responseText;
    constructor(status, responseText) {
        super(`Authentication service failure: ${status}`);
        this.status = status;
        this.responseText = responseText;
        this.name = 'WAuth2Service.ServiceFailureError';
    }
    toString() {
        return (`Error: Authentication service failure\n` +
            `\tStatus: ${this.status}\n` +
            `\tResponse:\n\t${this.responseText}`);
    }
}
function isAccessTokenErrorStatus(status) {
    return WAuth2Service.wrongCredentialsStatusCodes.includes(status);
}
async function handleServiceError(res) {
    if (res.status >= 400 && !isAccessTokenErrorStatus(res.status)) {
        throw new ServiceFailureError(res.status, await res.text());
    }
}
/** Performs requests to the Wolt Authentication service in a given environment. */
export class WAuth2Service {
    configuration;
    /** The error status codes that the access token endpoint can normally respond with
     * when wrong credentials are provided. */
    static wrongCredentialsStatusCodes = Object.values(AccessTokenErrorStatus);
    /** Represents an error that the access token endpoint can normally respond with
     * when wrong credentials are provided, e.g. from a 401 response. */
    static AccessTokenError = AccessTokenError;
    /** Represents an unexpected service error, e.g. from a 500 response. */
    static ServiceError = ServiceFailureError;
    /**
     * Calls Wolt Authentication's access token endpoint and resolves either with the access token data or
     * `AccessTokenError` in case if wrong credentials were provided. */
    static async fetchAccessTokenApi(serviceUrl, grant) {
        const headers = { 'content-type': 'application/x-www-form-urlencoded' };
        if ('hCaptcha' in grant && typeof grant.hCaptcha === 'string') {
            headers['h-captcha-response'] = grant.hCaptcha;
        }
        const response = await fetch(`${serviceUrl}/v1/wauth2/access_token`, {
            method: 'POST',
            headers,
            body: new URLSearchParams(snakeCaseKeys(grant)).toString(),
            credentials: 'include',
        });
        await handleServiceError(response);
        const json = await response.json();
        if (isAccessTokenErrorStatus(response.status)) {
            return new AccessTokenError(response.status, json);
        }
        return json;
    }
    /** Calls Wolt Authentication's logout endpoint. */
    static async fetchLogoutApi(serviceUrl, { accessToken, refreshToken }) {
        const response = await fetch(`${serviceUrl}/v1/wauth2/logout`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json; charset=utf-8',
                accept: 'application/json, text/plain, */*',
                authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ refresh_tokens: [refreshToken] }),
            credentials: 'include',
        });
        await handleServiceError(response);
        await response.json();
    }
    constructor(configuration) {
        this.configuration = configuration;
    }
    callAccessTokenApi(grant) {
        return WAuth2Service.fetchAccessTokenApi(this.configuration.serviceUrl, grant);
    }
    callLogoutApi(parameters) {
        return WAuth2Service.fetchLogoutApi(this.configuration.serviceUrl, parameters);
    }
}
/** @deprecated Use `WAuth2Service` instead */
export const WoltAuthenticationService = WAuth2Service;
